import React, { useEffect, useRef, useState } from "react";
import { X, Check } from "lucide-react";
import { BASE_URL } from "utils/constant";
import { useAlert } from "components/UI_components/Alert";

export default function AddVendor({ onClose }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [address, setAddress] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimerRef = useRef(null);
  const { addAlert } = useAlert();

  useEffect(() => {
    const raf = window.requestAnimationFrame(() => setIsMounted(true));
    return () => {
      window.cancelAnimationFrame(raf);
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const requestClose = () => {
    if (isClosing) return;
    setIsClosing(true);
    setIsMounted(false);
    closeTimerRef.current = window.setTimeout(() => {
      onClose?.();
    }, 260);
  };

  const handleNumberInput = (e) => {
    const onlyNums = e.target.value.replace(/[^0-9]/g, "");
    e.target.value = onlyNums;
    setContactNumber(onlyNums);
    if (onlyNums.length > 0 && onlyNums.length < 10) {
      setPhoneError("Contact number must be exactly 10 digits");
    } else {
      setPhoneError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    // 10-digit validation before API call
    if (contactNumber.length !== 10) {
      setPhoneError("Contact number must be exactly 10 digits");
      return;
    }
    setPhoneError("");

    try {
      setIsSubmitting(true);
      const accessToken = localStorage.getItem("access");
      const organisationId = JSON.parse(
        localStorage.getItem("organization"),
      ).id;
      const response = await fetch(`${BASE_URL}/inventory/create-vendor/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          name: name,
          vendor_type: type,
          address: address,
          contact_no: "+91" + contactNumber,
          organization: organisationId,
        }),
      });

      const data = await response.json();
      if (data.status) {
        console.log("Vendor created successfully:", data);
        requestClose();
        addAlert("Vendor created successfully", "success");
      } else {
        console.error("Error creating vendor:", data.error);
        addAlert("Error creating vendor: " + data.error, "error");
      }
    } catch (error) {
      console.error("Error creating vendor:", error);
      addAlert("Error creating vendor: " + error.message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
      <div
        className={`fixed inset-0 bg-black/40 flex items-stretch justify-end z-50 transition-opacity duration-300 ${isMounted && !isClosing ? "opacity-100" : "opacity-0"}`}
        onClick={requestClose}
      >
      <div
          className={`bg-white w-full sm:max-w-xl h-full flex flex-col rounded-l-xl shadow-2xl overflow-hidden transform transition-all duration-300 ease-out ${isMounted && !isClosing ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"}`}
        onClick={(e) => e.stopPropagation()}
      >
          <div className="relative flex items-center justify-center px-4 h-16 py-2 border-b border-gray-100 sticky top-0 bg-white z-10">
            <h2 className="text-xl font-medium text-[#232323]">New Vendor</h2>
            <button
              className="absolute right-4 w-8 h-8 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100"
              onClick={requestClose}
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6 pb-6">
            <div className="border border-gray-200 rounded-xl p-4 space-y-4">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              General Details
              </div>
              <div>
                <label className="block text-sm font-medium text-[#232323] mb-2">
                Vendor Name
              </label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter Name"
                className="w-full px-4 py-2 bg-[#F3F4F6] rounded-xl border-0 text-[#232323] focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#232323] mb-2">
                Select type
              </label>
              <input
                type="text"
                required
                value={type}
                onChange={(e) => setType(e.target.value)}
                placeholder="Enter Vendor type. (Eg. Brick supplier)"
                className="w-full px-4 py-2 bg-[#F3F4F6] rounded-xl border-0 text-[#232323] focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
                <div className="mt-3 flex flex-wrap gap-2">
                  {[
                    "Cement Supplier",
                    "Steel Supplier",
                    "Brick Supplier",
                    "Electricals",
                    "Plumbing",
                  ].map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setType(option)}
                      className={`px-3 py-1 rounded-full text-sm ${
                        type === option
                          ? "bg-[#ECE6F0] text-[#3B0069] border border-[#3B0069]"
                          : "bg-gray-100 border border-gray-200 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#232323] mb-2">
                Address
              </label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                required
                placeholder="Enter Address. (Saltlake, Kolkata, 700000)"
                className="w-full px-4 py-2 bg-[#F3F4F6] rounded-xl border-0 text-[#232323] focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#232323] mb-2">
                Contact number <span className="text-red-500">*</span>
              </label>
              <div
                className={`flex items-center bg-[#F3F4F6] px-4 py-2 rounded-xl border ${
                  phoneError ? "border-red-500" : "border-gray-200"
                }`}
              >
                <span className="mr-2 text-gray-600">+91</span>
                <input
                  type="text"
                  maxLength={10}
                  value={contactNumber}
                  onInput={handleNumberInput}
                  placeholder="9999999999"
                  required
                  className="w-full bg-transparent outline-none text-[#232323]"
                />
              </div>
              {phoneError && (
                <p className="text-red-500 text-sm mt-1">{phoneError}</p>
              )}
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-100 bg-white flex items-center justify-center shrink-0">
            <button
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                !name.trim() ||
                !type.trim() ||
                !address.trim() ||
                contactNumber.length !== 10
              }
              className={`min-w-[240px] bg-[#3B0069] hover:bg-[#3B0069D9] disabled:cursor-not-allowed text-white font-medium py-2 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 whitespace-nowrap ${
                isSubmitting ||
                !name.trim() ||
                !type.trim() ||
                !address.trim() ||
                contactNumber.length !== 10
                  ? "opacity-40"
                  : ""
              }`}
            >
              {isSubmitting ? "Creating..." : "Add Vendor"}
              <span className="rounded-full bg-[#58267F] p-1 flex items-center justify-center">
                <Check size={18} />
              </span>
            </button>
          </div>
      </div>
    </div>
  );
}
